import { ReactNode, useState } from 'react';
import { SvgIcon } from '../../components/icons';
import { STATUS_COLOR, STATUSES } from '../data/warrantyDemo';
import { useDsTheme } from '../ThemeProvider';
import { getTheme } from '../themes';
import type { ButtonVariant, ChipTone } from '../primitives';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  EmptyState,
  IconButton,
  Input,
  KpiCard,
  Radio,
  SearchField,
  SegmentedControl,
  Select,
  StatusChip,
  Switch,
  TabPanel,
  Tabs,
  Textarea,
  useTabsId,
  Tooltip,
  useToast,
} from '../primitives';

const Section = ({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) => (
  <Card style={{ marginBottom: 14 }}>
    <CardHeader>
      <div>
        <h2 className="ds-section-title">{title}</h2>
        {note && (
          <p className="ds-muted" style={{ fontSize: 'var(--ds-fs-xs)' }}>
            {note}
          </p>
        )}
      </div>
    </CardHeader>
    <CardBody>{children}</CardBody>
  </Card>
);

const VARIANTS: ButtonVariant[] = [
  'confirm',
  'primary',
  'secondary',
  'ghost',
  'danger',
];
const TONES: ChipTone[] = ['neutral', 'accent', 'ok', 'warn', 'danger', 'info'];

/**
 * Every primitive, in every state, under whichever theme is active.
 *
 * This is the "five versions of each component" deliverable: because the
 * components are token-driven, switching the theme in the settings panel
 * re-renders this whole page as that direction's version of the component set.
 */
export const ComponentGallery = () => {
  const { theme } = useDsTheme();
  const meta = getTheme(theme);
  const toast = useToast();

  const tabsId = useTabsId();
  const [tab, setTab] = useState(0);
  const [seg, setSeg] = useState<'day' | 'week' | 'month'>('week');
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <div className="ds-page-header">
        <div>
          <div className="ds-eyebrow">Component gallery</div>
          <h1 className="ds-title">{meta?.name}</h1>
          <p className="ds-subtitle">{meta?.tagline}</p>
        </div>
      </div>

      <Section
        title="Buttons"
        note="Five variants × three sizes. `confirm` is a distinct variant so a theme can give the commit action its own identity — Saga Gold renders it in gold."
      >
        <div className="flex flex-col gap-3">
          {(['lg', 'md', 'sm'] as const).map((size) => (
            <div key={size} className="flex flex-wrap items-center gap-2">
              <span
                className="ds-muted"
                style={{ width: 28, fontSize: 'var(--ds-fs-xs)' }}
              >
                {size}
              </span>
              {VARIANTS.map((v) => (
                <Button key={v} variant={v} size={size}>
                  {v}
                </Button>
              ))}
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <span
              className="ds-muted"
              style={{ width: 28, fontSize: 'var(--ds-fs-xs)' }}
            >
              states
            </span>
            <Button variant="primary" startIcon={<SvgIcon name="add" size={14} />}>
              With icon
            </Button>
            <Button variant="primary" loading>
              Loading
            </Button>
            <Button variant="primary" disabled>
              Disabled
            </Button>
            <Button variant="secondary" disabled>
              Disabled
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className="ds-muted"
              style={{ width: 28, fontSize: 'var(--ds-fs-xs)' }}
            >
              icon
            </span>
            {(['edit', 'delete', 'print', 'download', 'save'] as const).map((n) => (
              <IconButton key={n} label={n}>
                <SvgIcon name={n} size={16} />
              </IconButton>
            ))}
          </div>

          <p className="ds-muted" style={{ fontSize: 'var(--ds-fs-xs)' }}>
            Tab to a button and hold Enter or Space — the pressed state fires on
            keyboard as well as mouse, which plain <code>:active</code> does not
            reliably do.
          </p>
        </div>
      </Section>

      <Section
        title="Form controls"
        note="Native inputs throughout. Ids are generated per instance, so labels always point at their own field."
      >
        <div className="flex flex-wrap gap-3">
          <Input
            label="Tail number"
            defaultValue="TF-FIA"
            wrapperClassName="flex-1"
            style={{ minWidth: 150 }}
          />
          <Input
            label="With hint"
            placeholder="PO-000000"
            hint="Format: PO-######"
            wrapperClassName="flex-1"
          />
          <Input
            label="Invalid"
            defaultValue="bad value"
            error="Vendor reference is required."
            wrapperClassName="flex-1"
          />
          <Input
            label="Disabled"
            defaultValue="Locked"
            disabled
            wrapperClassName="flex-1"
          />
        </div>

        <div className="flex flex-wrap gap-3" style={{ marginTop: 12 }}>
          <Select
            label="Classification"
            wrapperClassName="flex-1"
            options={[
              { value: 'lht', label: 'LHT Contract' },
              { value: 'oem', label: 'OEM Direct' },
              { value: 'sb', label: 'Service Bulletin' },
            ]}
          />
          <SearchField
            label="Search"
            placeholder="Tail, MXI, PO…"
            wrapperClassName="flex-1"
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <Textarea label="Comment" rows={2} placeholder="Add a note…" />
        </div>

        <div className="flex flex-wrap gap-5" style={{ marginTop: 14 }}>
          <div className="flex flex-col gap-2">
            <Checkbox label="Checked" defaultChecked />
            <Checkbox label="Unchecked" />
            <Checkbox label="Disabled" disabled />
          </div>
          <div className="flex flex-col gap-2">
            <Radio name="g" label="Option A" defaultChecked />
            <Radio name="g" label="Option B" />
            <Radio name="g" label="Disabled" disabled />
          </div>
          <div className="flex flex-col gap-2">
            <Switch label="On" defaultChecked />
            <Switch label="Off" />
            <Switch label="Disabled" disabled />
          </div>
        </div>
      </Section>

      <Section
        title="Chips, badges and fixed status colours"
        note="Status hues are frozen by the domain. They render as a dot beside a text label, so the mandated colour is untouched, never fights the theme for contrast, and is never the only signal."
      >
        <div className="flex flex-wrap gap-2">
          {TONES.map((t) => (
            <Chip key={t} tone={t}>
              {t}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" style={{ marginTop: 10 }}>
          {TONES.map((t) => (
            <Chip key={t} tone={t} variant="outline">
              {t}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" style={{ marginTop: 10 }}>
          {STATUSES.map((s) => (
            <StatusChip key={s} color={STATUS_COLOR[s]} label={s} />
          ))}
        </div>
      </Section>

      <Section title="Tabs and segmented control">
        <Tabs
          tabs={['Claims', 'Approvals', 'Activity', 'Archive']}
          value={tab}
          onChange={setTab}
          idBase={tabsId}
        />
        {['Claims', 'Approvals', 'Activity', 'Archive'].map((t, i) => (
          <TabPanel key={t} baseId={tabsId} index={i} active={i === tab}>
            <p className="ds-muted" style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 8 }}>
              Panel content for <strong>{t}</strong>.
            </p>
          </TabPanel>
        ))}
        <p className="ds-muted" style={{ fontSize: 'var(--ds-fs-xs)', margin: '8px 0 14px' }}>
          Arrow keys move between tabs; only the active tab is in the tab order.
        </p>
        <SegmentedControl
          label="Range"
          value={seg}
          onChange={setSeg}
          options={[
            { value: 'day', label: 'Day' },
            { value: 'week', label: 'Week' },
            { value: 'month', label: 'Month' },
          ]}
        />
      </Section>

      <Section title="Stat tiles">
        <div className="flex flex-wrap gap-3">
          <div style={{ flex: '1 1 170px' }}>
            <KpiCard label="Open claims" value="14" delta="+3" direction="up" />
          </div>
          <div style={{ flex: '1 1 170px' }}>
            <KpiCard label="Recovery" value="61%" delta="-2.1 pts" direction="down" />
          </div>
          <div style={{ flex: '1 1 170px' }}>
            <KpiCard label="Avg. days" value="17.4" delta="unchanged" direction="flat" />
          </div>
        </div>
      </Section>

      <Section title="Overlays and feedback">
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setDialogOpen(true)}>
            Open dialog
          </Button>
          <Button
            variant="secondary"
            onClick={() => toast.push({ title: 'Saved', tone: 'ok' })}
          >
            Success toast
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              toast.push({
                title: 'Vendor unreachable',
                body: 'Retrying in 30 seconds.',
                tone: 'danger',
              })
            }
          >
            Error toast
          </Button>
          <Tooltip label="Tooltips appear on focus too">
            <Button variant="ghost">Hover or focus me</Button>
          </Tooltip>
        </div>
      </Section>

      <Section title="Empty state">
        <EmptyState
          icon={<SvgIcon name="search" size={20} />}
          title="No claims match these filters"
          hint="Try widening the tail or status filter, or clear them all to start again."
          action={
            <Button variant="secondary" size="sm">
              Clear filters
            </Button>
          }
        />
      </Section>

      <Dialog
        open={dialogOpen}
        title="Dialog"
        onClose={() => setDialogOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="confirm" onClick={() => setDialogOpen(false)}>
              Confirm
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: 12 }}>
          Focus is trapped here and returns to the trigger on close. Escape
          closes unless the dialog is marked strict.
        </p>
        <Input label="A field to Tab into" placeholder="Try Tab and Shift+Tab" />
      </Dialog>
    </>
  );
};
